import Button from "components/button"
import useMainClass from "hooks/use-main-cass"
import { useContext, useEffect, useState } from "react"
import { LoaderContext } from "contexts/loader-context"
import ProfileService from "services/profile-services"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { Checkbox, DivCheckbox, Label, PCheckbox, ErrorInput, SelectGender } from 'theme/theme'
import Input from 'components/input'
import formServices from "services/form-builder-services"
import textServices from "services/text-services"
import { ErrorMessage } from "@hookform/error-message"
import { dlTrackEvent } from "components/dataLayer/dataLayer"
import utmServices from "services/utm-services"
import { useAgegate } from "hooks/use-agegate"
import toast from "react-hot-toast"
import { IBodyLoginExternal } from "../auth"

interface IProps {
  setStepper: React.Dispatch<React.SetStateAction<'start' | 'form'>>
  setError: (error: string | number) => void
  handleLoginExternal: (body: IBodyLoginExternal) => Promise<void>
}

export default function ViewForm({ handleLoginExternal }: IProps) {
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({ resolver: yupResolver(formServices.formGenerateSchema(['firstName', 'phoneNumber', 'email', 'termsOfUse', 'privacyPolicy', 'genreId'])) })
  const [gender, setGender] = useState([])
  const { setLoad } = useContext(LoaderContext)
  const termsOfUse = watch('termsOfUse')
  const privacyPolicy = watch('privacyPolicy')
  const subscribe = watch('subscribe')
  const { setAgeLocalForm } = useAgegate()
  useMainClass('wifi__auth__form')

  const handleTermsOfUse = () => {
    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'MODIFICAR INFORMACION - Acepto terminos y condiciones de privacidad',
      value: '10'
    })

    dlTrackEvent('user_interaction', {
      action: 'select',
      element: 'Acepto terminos y condiciones',
      section: 'sign up'
    })

    setValue('termsOfUse', !termsOfUse)
  }

  const handlePrivacyPolicy = () => {
    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'MODIFICAR INFORMACION - Acepto condiciones de privacidad',
      value: '10'
    })

    dlTrackEvent('user_interaction', {
      action: 'select',
      element: 'Acepto condiciones de privacidad',
      section: 'sign up'
    })

    setValue('privacyPolicy', !privacyPolicy)
  }

  const handleSubscribe = () => {
    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'MODIFICAR INFORMACION - Acepto recibir novedades',
      value: '10'
    })

    dlTrackEvent('user_interaction', {
      action: 'select',
      element: 'Acepto recibir novedades',
      section: 'sign up'
    })

    setValue('subscribe', !subscribe)
  }

  async function getGenres() {
    setLoad(true)
    try {
      const response = await ProfileService.genres()
      setGender(response.data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoad(false)
    }
  }

  useEffect(() => {
    getGenres()
  }, [])

  const submit = async (data: any) => {
    const utm = utmServices.utmRead()

    const birthday = new Date(data?.birthdayDate)
    const response = setAgeLocalForm(birthday)
    if (response?.error) return toast.error(response?.error, { className: 'toast__error' })

    await handleLoginExternal({
      email: data?.email,
      firstName: data?.firstName,
      provider: 'Email',
      birthday: data?.birthdayDate,
      genreId: data?.genreId,
      isLogin: false,
      utm,
      phone: data?.phoneNumber,
      subscribe: data?.subscribe,
      termsOfUse: data?.privacyPolicy
    })
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="w-full my-8 px-4 flex flex-col items-center flex-1 gap-4">
      <p className="uppercase description medium"><b>Completa los datos para ingresar</b></p>
      <div className="flex-1 space-y-3 w-full">
        <div className="gap-2 flex flex-col input-container">
          <Label className="text-start text-2xl font-bold uppercase input-label">
            {textServices.findByText('profile/form/information/firstname/label', {
              es: '¿Cómo te llamas?',
              en: 'What is your name?'
            })}
          </Label>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder={textServices.findByText('profile/form/information/firstname/placeholder', {
              es: 'PON TU NOMBRE AQUÍ',
              en: 'PUT YOUR NAME HERE'
            })}
            name="firstName"
            {...register('firstName')}
          />
          <ErrorInput className="input-error">
            <ErrorMessage errors={errors} name="firstName" />
          </ErrorInput>
        </div>
        <div className={`gap-2 flex flex-col input-container`}>
          <Label className="text-start text-2xl font-bold uppercase input-label">
            {textServices.findByText('profile/form/information/birthday/label', { es: 'Fecha de nacimiento', en: 'Date of birthday' })}
          </Label>
          <input
            type='date'
            className='input-date w-full'
            {...register('birthdayDate', {
              onBlur: (e) => {
                dlTrackEvent('form_interaction', {
                  form_field: 'birthday',
                  form_name: 'datos personales',
                  form_action: e.target.value ? 'complete' : 'empty',
                  section: 'sign up :: step 2'
                })
              }
            })}
          />
          <ErrorInput className="input-error">
            <ErrorMessage errors={errors} name="birthdayDate" />
          </ErrorInput>
        </div>
        <div className="gap-2 flex flex-col input-container">
          <Label className="text-start text-2xl font-bold uppercase input-label">
            {textServices.findByText('profile/form/information/gender/label', {
              es: '¿Cómo te identificas?',
              en: 'How do you identify yourself?'
            })}
          </Label>
          <SelectGender
            name="genreId"
            {...register('genreId', {
              onBlur: (e) => {
                dlTrackEvent('form_interaction', {
                  form_field: 'genero',
                  form_action: e.target.value ? 'complete' : 'empty',
                  form_name: 'datos personales',
                  section: 'sign up :: step 2'
                })
              }
            })}
            className="text-xs font-bold p-4 w-full min-h-2 mx-auto saira uppercase input-select"
          >
            <option value="">
              {textServices.findByText('profile/form/information/gender/placeholder', {
                es: 'Selecciona como te consideras',
                en: 'Select how you consider yourself'
              })}
            </option>
            {gender?.map((item: { id: number; name: string }) => (
              <option key={item.id} value={item.id}>
                {item?.name}
              </option>
            ))}
          </SelectGender>
          <ErrorInput className="input-error">
            <ErrorMessage errors={errors} name="genreId" />
          </ErrorInput>
        </div>
        <div className="gap-2 flex flex-col input-container">
          <Label className="text-start text-2xl font-bold uppercase input-label">
            {textServices.findByText('profile/form/information/email/label', {
              es: 'Correo electrónico',
              en: 'E-mail address'
            })}
          </Label>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder={textServices.findByText('profile/form/information/email/placeholder', {
              es: 'roberto@gmail.com',
              en: 'robert@gmail.com'
            })}
            name="email"
            {...register('email')}
          />
          <ErrorInput className="input-error">
            <ErrorMessage errors={errors} name="email" />
          </ErrorInput>
        </div>
        <div className="gap-2 flex flex-col input-container">
          <Label className="text-start text-2xl font-bold uppercase input-label">{textServices.findByText('otp/phone/title', { es: 'Número de celular', en: 'Phone number' })}</Label>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder="Ingresa tu número"
            name="phoneNumber"
            {...register("phoneNumber")}
          />
          <ErrorInput className="input-error">
            <ErrorMessage errors={errors} name="phoneNumber" />
          </ErrorInput>
        </div>
        <div className="flex flex-col gap-2 w-full">
          <DivCheckbox className="gap-4 flex items-center cursor-pointer input-container" onClick={handleTermsOfUse}>
            <Checkbox active={termsOfUse} className={`h-6 w-6 flex items-center justify-center`} id="termsOfUse" />
            <PCheckbox className="dinPro input-checkbox-label flex-1">
              {textServices.findByText('signup/termsOfUse/label', {
                es: (
                  <>
                    Acepto los{' '}
                    <a
                      href={`${process.env.PUBLIC_URL}/terms-and-conditions`}
                      rel="noreferrer"
                      target="_blank"
                      className="underline"
                    >
                      Términos y Condiciones
                    </a>
                    .
                  </>
                ),
                en: (
                  <>
                    I accept the{' '}
                    <a
                      href={`${process.env.PUBLIC_URL}/terms-and-conditions`}
                      rel="noreferrer"
                      target="_blank"
                      className="underline"
                    >
                      Terms and Conditions
                    </a>
                    .
                  </>
                )
              })}
            </PCheckbox>
          </DivCheckbox>
          <ErrorInput className="input-error">
            <ErrorMessage errors={errors} name="termsOfUse" />
          </ErrorInput>
          <DivCheckbox className="gap-4 flex items-center cursor-pointer input-container" onClick={handlePrivacyPolicy}>
            <Checkbox active={privacyPolicy} className={`h-6 w-6 flex items-center justify-center`} id="privacyPolicy" />
            <PCheckbox className="dinPro input-checkbox-label flex-1">
              {textServices.findByText('signup/privacyPolicy/label', {
                es: (
                  <>
                    Acepto el{' '}
                    <a
                      href={`${process.env.PUBLIC_URL}/privacy-politics`}
                      rel="noreferrer"
                      target="_blank"
                      className="underline"
                    >
                      Aviso de privacidad
                    </a>
                    .
                  </>
                ),
                en: (
                  <>
                    I accept the{' '}
                    <a
                      href={`${process.env.PUBLIC_URL}/privacy-politics`}
                      rel="noreferrer"
                      target="_blank"
                      className="underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </>
                )
              })}
            </PCheckbox>
          </DivCheckbox>
          <ErrorInput className="input-error">
            <ErrorMessage errors={errors} name="privacyPolicy" />
          </ErrorInput>
          <DivCheckbox className="gap-4 flex items-center cursor-pointer input-container" onClick={handleSubscribe}>
            <Checkbox className={`h-6 w-6 flex items-center justify-center`} id="suscribe" active={subscribe} />
            <PCheckbox className="flex-1 dinPro input-checkbox-label">
              {textServices.findByText('signup/suscribe/label', {
                es: (
                  <>
                    Tu privacidad es nuestra prioridad. Al &quot;Aceptar&quot;, nos das tu consentimiento para procesar
                    tus datos personales con el fin de mejorar tu experiencia y ofrecerte contenido personalizado. En
                    cualquier momento, puedes gestionar o retirar tu consentimiento accediendo a tu <b>Preference Center</b> o a
                    través de los medios que ponemos a tu disposición en el{' '}
                    <a
                      href={`${process.env.PUBLIC_URL}/privacy-politics`}
                      rel="noreferrer"
                      target="_blank"
                      className="underline"
                    >
                      Aviso de Privacidad
                    </a>
                    .
                  </>
                ),
                en: (
                  <>
                    Your privacy is our priority. By &quot;Accepting&quot;, you give us your consent to process your
                    personal data in order to improve your experience and offer you personalized content. At any time, you
                    can manage or withdraw your consent by accessing your <b>Preference Center</b> or through the means we make
                    available to you in the{' '}
                    <a
                      href={`${process.env.PUBLIC_URL}/privacy-politics`}
                      rel="noreferrer"
                      target="_blank"
                      className="underline"
                    >
                      Privacy Notice
                    </a>
                    .
                  </>
                )
              })}
            </PCheckbox>
          </DivCheckbox>
          <ErrorInput className="input-error">
            <ErrorMessage errors={errors} name="subscribe" />
          </ErrorInput>
        </div>
      </div>
      <Button>SIGUIENTE</Button>
    </form>
  )
}